import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
    ArrowsClockwise,
    CheckCircle,
    MagnifyingGlass,
    UsersThree,
    Database,
    Info,
} from "@phosphor-icons/react";
import {
    listMappingPlayers,
    listMappingTeams,
    rebuildMappings,
    verifyPlayerMapping,
    getSportmonksStatus,
} from "../lib/api";

// -- visual config ----------------------------------------------------------

const CONF_STYLE = {
    VERIFIED: { bg: "#9EE8B4", text: "#050505", label: "VERIFIED" },
    HIGH: { bg: "#9DB7F5", text: "#050505", label: "HIGH" },
    MEDIUM: { bg: "#FFF3C2", text: "#050505", label: "MEDIUM" },
    LOW: { bg: "#FF8800", text: "#050505", label: "LOW" },
    NONE: { bg: "#6B7280", text: "#ffffff", label: "NONE" },
};

const CONF_ORDER = ["VERIFIED", "HIGH", "MEDIUM", "LOW", "NONE"];

function ConfChip({ value }) {
    const s = CONF_STYLE[value] || CONF_STYLE.NONE;
    return (
        <span
            data-testid={`conf-chip-${value}`}
            className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 inline-block"
            style={{ backgroundColor: s.bg, color: s.text }}
        >
            {s.label}
        </span>
    );
}

// -- main page --------------------------------------------------------------

export default function Mapping() {
    const [tab, setTab] = useState("players");
    const [players, setPlayers] = useState({ items: [], by_confidence: {}, total_in_db: 0 });
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const [confFilter, setConfFilter] = useState(["MEDIUM", "LOW", "NONE"]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [verifying, setVerifying] = useState(null); // player object or null
    const [sportmonks, setSportmonks] = useState(null);

    // Debounce search
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 250);
        return () => clearTimeout(id);
    }, [search]);

    // When user starts typing a search, auto-expand the filter to all tiers
    // so high-confidence matches (e.g. "Salah") aren't hidden by default.
    useEffect(() => {
        if (debouncedSearch && confFilter.length < CONF_ORDER.length) {
            setConfFilter([...CONF_ORDER]);
        }
        // intentionally only re-runs on search change — toggling filters
        // manually after typing is allowed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    const loadPlayers = useCallback(async () => {
        setLoading(true);
        try {
            const d = await listMappingPlayers({
                confidence: confFilter,
                q: debouncedSearch || undefined,
                limit: 300,
            });
            setPlayers(d);
        } catch (e) {
            toast.error("Failed to load player mappings");
        } finally {
            setLoading(false);
        }
    }, [confFilter, debouncedSearch]);

    const loadTeams = useCallback(async () => {
        try {
            const d = await listMappingTeams();
            setTeams(d);
        } catch (e) {
            toast.error("Failed to load team mappings");
        }
    }, []);

    useEffect(() => {
        loadPlayers();
        loadTeams();
        getSportmonksStatus()
            .then(setSportmonks)
            .catch(() => setSportmonks({ configured: false, error: true }));
    }, [loadPlayers, loadTeams]);

    const handleRebuild = async () => {
        setRebuilding(true);
        try {
            const r = await rebuildMappings(true);
            toast.success(
                `Rebuilt: ${r.players.total} players · HIGH ${r.players.by_confidence.HIGH} · MED ${r.players.by_confidence.MEDIUM}`,
            );
            await Promise.all([loadPlayers(), loadTeams()]);
        } catch (e) {
            toast.error("Rebuild failed");
        } finally {
            setRebuilding(false);
        }
    };

    const total = players.total_in_db || 0;
    const hist = players.by_confidence || {};

    return (
        <AppShell>
            <div className="space-y-6" data-testid="mapping-page">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-yellow">
                            Phase C · Provider Mapping
                        </div>
                        <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">
                            Mapping Review
                        </h1>
                        <p className="text-white/50 text-sm mt-1 max-w-xl">
                            FPL ↔ Fantasy Nutmeg ↔ Sportmonks. Confirm fuzzy matches manually for
                            historical & advanced-stats enrichment.
                        </p>
                    </div>
                    <Button
                        onClick={handleRebuild}
                        disabled={rebuilding}
                        data-testid="rebuild-button"
                        className="bg-fa-yellow hover:bg-fa-yellow/90 text-black font-mono text-xs uppercase tracking-widest h-10 rounded-none"
                    >
                        <ArrowsClockwise size={14} weight="bold" className={rebuilding ? "animate-spin mr-2" : "mr-2"} />
                        {rebuilding ? "Rebuilding…" : "Rebuild from FPL + Nutmeg"}
                    </Button>
                </div>

                {/* Sportmonks plan status banner */}
                {sportmonks && <SportmonksBanner status={sportmonks} />}

                {/* Histogram */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="conf-histogram">
                    {CONF_ORDER.map((c) => {
                        const n = hist[c] || 0;
                        const pct = total ? Math.round((n / total) * 100) : 0;
                        const s = CONF_STYLE[c];
                        return (
                            <div
                                key={c}
                                className="border border-white/10 bg-black/40 p-4"
                                data-testid={`hist-${c}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div
                                        className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5"
                                        style={{ backgroundColor: s.bg, color: s.text }}
                                    >
                                        {c}
                                    </div>
                                    <span className="font-mono text-[10px] text-white/40">{pct}%</span>
                                </div>
                                <div className="font-display font-black text-3xl mt-2">{n}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-white/10">
                    <TabButton active={tab === "players"} onClick={() => setTab("players")} icon={<UsersThree size={14} weight="bold" />} testid="tab-players">
                        Players ({total})
                    </TabButton>
                    <TabButton active={tab === "teams"} onClick={() => setTab("teams")} icon={<Database size={14} weight="bold" />} testid="tab-teams">
                        Teams ({teams.length})
                    </TabButton>
                </div>

                {tab === "players" ? (
                    <PlayersPanel
                        players={players}
                        loading={loading}
                        confFilter={confFilter}
                        setConfFilter={setConfFilter}
                        search={search}
                        setSearch={setSearch}
                        onVerify={setVerifying}
                    />
                ) : (
                    <TeamsPanel teams={teams} />
                )}

                {verifying && (
                    <VerifyDialog
                        player={verifying}
                        onClose={() => setVerifying(null)}
                        onSaved={() => {
                            setVerifying(null);
                            loadPlayers();
                        }}
                    />
                )}
            </div>
        </AppShell>
    );
}

function SportmonksBanner({ status }) {
    if (!status.configured) {
        return (
            <div className="border border-white/10 bg-black/40 p-3 flex items-start gap-3" data-testid="sportmonks-banner-missing">
                <Info size={16} weight="bold" className="text-white/40 mt-0.5 flex-shrink-0" />
                <div className="font-mono text-[11px] text-white/60 leading-relaxed">
                    <span className="text-white/80 uppercase tracking-widest">Sportmonks not configured</span>
                    {" — "}set <code className="text-fa-yellow">SPORTMONKS_API_KEY</code> in <code className="text-fa-yellow">backend/.env</code> to enable Sportmonks enrichment.
                </div>
            </div>
        );
    }
    if (!status.epl_in_plan) {
        const planLabel = (status.plans || []).join(", ") || "unknown plan";
        return (
            <div className="border border-fa-yellow/40 bg-fa-yellow/5 p-3 flex items-start gap-3" data-testid="sportmonks-banner-no-epl">
                <Info size={16} weight="bold" className="text-fa-yellow mt-0.5 flex-shrink-0" />
                <div className="font-mono text-[11px] text-white/80 leading-relaxed">
                    <span className="text-fa-yellow uppercase tracking-widest">Sportmonks: {planLabel}</span>
                    {" — "}English Premier League is <span className="text-fa-yellow">NOT</span> in this plan.
                    Accessible leagues: {(status.accessible_leagues || []).map((l) => l.name).join(", ") || "none"}. Upgrade to the European Plan (~€25/mo) to unlock EPL enrichment.
                </div>
            </div>
        );
    }
    return (
        <div className="border border-[#9EE8B4]/40 bg-[#9EE8B4]/5 p-3 flex items-start gap-3" data-testid="sportmonks-banner-ok">
            <CheckCircle size={16} weight="fill" className="text-[#9EE8B4] mt-0.5 flex-shrink-0" />
            <div className="font-mono text-[11px] text-white/80 leading-relaxed">
                <span className="text-[#9EE8B4] uppercase tracking-widest">Sportmonks active</span>
                {" — "}English Premier League in plan ({(status.plans || []).join(", ") || "—"}). Sportmonks enrichment will run on rebuild.
            </div>
        </div>
    );
}

function TabButton({ active, onClick, icon, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border-b-2 -mb-[2px] flex items-center gap-2 ${active
                ? "border-fa-yellow text-fa-yellow"
                : "border-transparent text-white/60 hover:text-white"
                }`}
        >
            {icon}
            {children}
        </button>
    );
}

// -- players panel ----------------------------------------------------------

function PlayersPanel({ players, loading, confFilter, setConfFilter, search, setSearch, onVerify }) {
    const toggleConf = (c) => {
        setConfFilter((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
    };

    return (
        <div>
            {/* Filter row */}
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4" data-testid="player-filters">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Filter:</span>
                    {CONF_ORDER.map((c) => {
                        const on = confFilter.includes(c);
                        const s = CONF_STYLE[c];
                        return (
                            <button
                                key={c}
                                type="button"
                                onClick={() => toggleConf(c)}
                                data-testid={`filter-${c}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${on
                                    ? "border-transparent"
                                    : "border-white/20 bg-transparent text-white/40 hover:text-white"
                                    }`}
                                style={on ? { backgroundColor: s.bg, color: s.text } : undefined}
                            >
                                {c}
                            </button>
                        );
                    })}
                </div>
                <div className="relative w-full md:w-72">
                    <MagnifyingGlass
                        size={14}
                        weight="bold"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                    />
                    <Input
                        type="text"
                        placeholder="Search by name…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="search-input"
                        className="pl-9 bg-black/40 border-white/10 rounded-none font-mono text-xs"
                    />
                </div>
            </div>

            {loading ? (
                <div className="font-mono text-xs text-white/40 py-12 text-center">Loading…</div>
            ) : players.items.length === 0 ? (
                <div className="font-mono text-xs text-white/40 py-12 text-center" data-testid="empty-state">
                    No mappings match the current filter. Try adjusting filters or run rebuild.
                </div>
            ) : (
                <div className="border border-white/10 overflow-x-auto" data-testid="players-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-white/5 border-b border-white/10 text-left">
                                <Th>FPL Player</Th>
                                <Th>Team</Th>
                                <Th>Pos</Th>
                                <Th>Nutmeg Match</Th>
                                <Th>Score</Th>
                                <Th>Confidence</Th>
                                <Th>Source</Th>
                                <Th>Action</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {players.items.map((p) => (
                                <tr
                                    key={p.fpl_id}
                                    className="border-b border-white/5 hover:bg-white/[0.03]"
                                    data-testid={`row-player-${p.fpl_id}`}
                                >
                                    <Td>
                                        <div className="font-display font-bold">{p.fpl_web_name}</div>
                                        <div className="text-white/40 text-[11px]">
                                            {p.fpl_first_name} {p.fpl_second_name}
                                        </div>
                                    </Td>
                                    <Td className="font-mono text-xs">{p.fpl_team_short}</Td>
                                    <Td className="font-mono text-xs">{p.fpl_position}</Td>
                                    <Td className="font-mono text-xs">
                                        {p.nutmeg_name || <span className="text-white/30">—</span>}
                                    </Td>
                                    <Td className="font-mono text-xs">
                                        {p.nutmeg_score > 0 ? p.nutmeg_score.toFixed(1) : "—"}
                                    </Td>
                                    <Td>
                                        <ConfChip value={p.nutmeg_confidence} />
                                    </Td>
                                    <Td className="font-mono text-[10px] uppercase text-white/50">
                                        {p.source}
                                        {p.source === "manual" && <CheckCircle size={12} weight="fill" className="inline ml-1 text-fa-yellow" />}
                                    </Td>
                                    <Td>
                                        <button
                                            type="button"
                                            onClick={() => onVerify(p)}
                                            data-testid={`verify-btn-${p.fpl_id}`}
                                            className="font-mono text-[10px] uppercase tracking-widest px-3 py-1 border border-white/20 hover:border-fa-yellow hover:text-fa-yellow transition-colors"
                                        >
                                            Review
                                        </button>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="bg-white/[0.02] px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white/40 border-t border-white/10">
                        Showing {players.returned} of {players.total_in_db} mappings
                    </div>
                </div>
            )}
        </div>
    );
}

// -- teams panel ------------------------------------------------------------

function TeamsPanel({ teams }) {
    return (
        <div className="border border-white/10 overflow-x-auto" data-testid="teams-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-white/5 border-b border-white/10 text-left">
                        <Th>FPL Team</Th>
                        <Th>Short</Th>
                        <Th>Aliases</Th>
                        <Th>Sportmonks</Th>
                        <Th>Source</Th>
                    </tr>
                </thead>
                <tbody>
                    {teams.map((t) => (
                        <tr
                            key={t.fpl_id}
                            className="border-b border-white/5 hover:bg-white/[0.03]"
                            data-testid={`row-team-${t.fpl_id}`}
                        >
                            <Td className="font-display font-bold">{t.fpl_name}</Td>
                            <Td className="font-mono text-xs">{t.fpl_short}</Td>
                            <Td className="font-mono text-[10px] text-white/60">
                                {(t.aliases || []).join(", ") || "—"}
                            </Td>
                            <Td className="font-mono text-xs text-white/40">
                                {t.sportmonks_id || <span title="Not yet integrated">—</span>}
                            </Td>
                            <Td className="font-mono text-[10px] uppercase text-white/50">{t.source}</Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const Th = ({ children }) => (
    <th className="font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2">
        {children}
    </th>
);

const Td = ({ children, className = "" }) => (
    <td className={`px-3 py-2 align-top ${className}`}>{children}</td>
);

// -- verify dialog ----------------------------------------------------------

function VerifyDialog({ player, onClose, onSaved }) {
    const [nutmegName, setNutmegName] = useState(player.nutmeg_name || "");
    const [sportmonksId, setSportmonksId] = useState(player.sportmonks_id || "");
    const [notes, setNotes] = useState(player.notes || "");
    const [saving, setSaving] = useState(false);
    const alternatives = player.nutmeg_alternatives || [];

    const handleSave = async () => {
        setSaving(true);
        try {
            await verifyPlayerMapping(player.fpl_id, {
                nutmeg_name: nutmegName || null,
                sportmonks_id: sportmonksId ? Number(sportmonksId) : null,
                notes: notes || null,
            });
            toast.success(`${player.fpl_web_name} marked VERIFIED`);
            onSaved();
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-[#0a0a0a] border-white/10 rounded-none max-w-lg" data-testid="verify-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-tight text-xl">
                        Verify Mapping
                    </DialogTitle>
                    <DialogDescription className="text-white/50 font-mono text-xs">
                        FPL #{player.fpl_id} · {player.fpl_first_name} {player.fpl_second_name} ({player.fpl_team_short} · {player.fpl_position})
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Alternatives */}
                    {alternatives.length > 0 && (
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                                Top auto-match candidates
                            </Label>
                            <div className="flex flex-wrap gap-2 mt-2" data-testid="alternatives-list">
                                {alternatives.map((alt) => (
                                    <button
                                        key={alt.name}
                                        type="button"
                                        onClick={() => setNutmegName(alt.name)}
                                        data-testid={`alt-${alt.name}`}
                                        className={`font-mono text-[11px] px-2 py-1 border transition-colors ${nutmegName === alt.name
                                            ? "border-fa-yellow text-fa-yellow bg-fa-yellow/10"
                                            : "border-white/20 hover:border-white/50"
                                            }`}
                                    >
                                        {alt.name}
                                        <span className="text-white/40 ml-2">{alt.score.toFixed(0)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                            Fantasy Nutmeg name
                        </Label>
                        <Input
                            value={nutmegName}
                            onChange={(e) => setNutmegName(e.target.value)}
                            placeholder="exact Nutmeg-side name…"
                            data-testid="nutmeg-name-input"
                            className="mt-1 bg-black/40 border-white/10 rounded-none font-mono text-xs"
                        />
                    </div>

                    <div>
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                            Sportmonks ID (optional)
                        </Label>
                        <Input
                            type="number"
                            value={sportmonksId}
                            onChange={(e) => setSportmonksId(e.target.value)}
                            placeholder="numeric Sportmonks player_id"
                            data-testid="sportmonks-id-input"
                            className="mt-1 bg-black/40 border-white/10 rounded-none font-mono text-xs"
                        />
                    </div>

                    <div>
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                            Notes
                        </Label>
                        <Input
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="why this match? edge cases…"
                            data-testid="notes-input"
                            className="mt-1 bg-black/40 border-white/10 rounded-none font-mono text-xs"
                        />
                    </div>

                    <div className="flex items-start gap-2 text-white/40 font-mono text-[10px] leading-relaxed">
                        <Info size={12} weight="bold" className="mt-0.5 flex-shrink-0" />
                        <span>
                            Marking as VERIFIED locks this row from auto-rebuild. To re-trigger fuzzy matching, clear the Nutmeg name and save.
                        </span>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="cancel-verify"
                        className="bg-transparent border-white/20 hover:bg-white/5 rounded-none font-mono text-xs uppercase"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-verify"
                        className="bg-fa-yellow hover:bg-fa-yellow/90 text-black rounded-none font-mono text-xs uppercase"
                    >
                        {saving ? "Saving…" : "Save & Verify"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
